import React, { useEffect, useState } from "react";
import { requiresUploadBlobAuth, fetchUploadBlob } from "@/api/apiClient";

/**
 * Imagens salvas via POST /api/upload ficam em URLs como /api/upload/:id ou
 * https://api.../api/upload/:id. O GET exige JWT — o navegador não envia o header
 * em <img src="...">, então a imagem quebra. Este componente busca o blob com
 * token e exibe via object URL.
 */
function needsAuthenticatedFetch(src) {
  if (!src || typeof src !== "string") return false;
  if (src.startsWith("data:") || src.startsWith("blob:")) return false;
  return requiresUploadBlobAuth(src);
}

export default function AuthenticatedUploadImage({
  src,
  alt = "",
  className,
  style,
  loadingClassName = "h-16 w-full max-w-md mx-auto rounded bg-slate-100 animate-pulse",
  ...rest
}) {
  const [displaySrc, setDisplaySrc] = useState(() =>
    src && !needsAuthenticatedFetch(src) ? src : null
  );
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) {
      setDisplaySrc(null);
      setError(false);
      return;
    }
    if (!needsAuthenticatedFetch(src)) {
      setDisplaySrc(src);
      setError(false);
      return;
    }

    let revoked = false;
    let objectUrl = null;
    setError(false);
    setDisplaySrc(null);

    (async () => {
      try {
        const blob = await fetchUploadBlob(src);
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setDisplaySrc(objectUrl);
      } catch (e) {
        console.error("[AuthenticatedUploadImage]", e);
        if (!revoked) setError(true);
      }
    })();

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src) return null;
  if (error) {
    return (
      <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
        Não foi possível carregar a imagem (acesse logado ou verifique o arquivo).
      </span>
    );
  }
  if (!displaySrc) {
    return <div className={loadingClassName} aria-hidden="true" />;
  }

  return (
    <img src={displaySrc} alt={alt} className={className} style={style} {...rest} />
  );
}
